import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ExistingGuest } from 'voyager';

type GuestFormValues = {
  title: string;
  firstName: string;
  lastName: string;
  gender: string;
  selectedGuestId?: string;
  id?: number | string;
  externalSpaId?: number | string | null;
  loggedInUser?: { id: number };
};

type HandleExistingGuestSelectOptions = {
  form: UseFormReturn<any>;
  existingGuests: ExistingGuest[];
  titleLabelToValue?: Record<string, string>;
  defaultGender?: string;
  setValueOptions?: { shouldValidate?: boolean; shouldDirty?: boolean };
};

export const useExistingGuestSelect = ({
  form,
  existingGuests,
  titleLabelToValue,
  defaultGender = '',
  setValueOptions,
}: HandleExistingGuestSelectOptions) =>
  useCallback(
    (guestIndex: number, guestId: string | undefined) => {
      const writeRow = (row: Partial<GuestFormValues>) => {
        (Object.entries(row) as [keyof GuestFormValues, unknown][]).forEach(([key, value]) => {
          form.setValue(`guests.${guestIndex}.${key}`, value, setValueOptions);
        });
      };

      if (guestId) {
        const selected = existingGuests.find((g) => g.id === guestId);
        if (!selected) return;

        const title = titleLabelToValue
          ? titleLabelToValue[selected.title] || selected.title
          : selected.title;

        writeRow({
          title,
          firstName: selected.firstName,
          lastName: selected.lastName,
          gender: selected.gender,
          selectedGuestId: guestId,
          externalSpaId: selected.externalSpaId,
          id: !selected.loggedInUser && selected.guestId != null ? selected.guestId : undefined,
          loggedInUser: selected.loggedInUser,
        });
        return;
      }

      writeRow({
        title: '',
        firstName: '',
        lastName: '',
        gender: defaultGender,
        selectedGuestId: undefined,
        id: undefined,
        externalSpaId: undefined,
        loggedInUser: undefined,
      });
    },
    [form, existingGuests, titleLabelToValue, defaultGender, setValueOptions]
  );
